"use client";

import type { VariantProps } from "class-variance-authority";
import { type PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "../../utils";
import { type OTPRootContext, OTPRootContextProvider } from "./context";
import type { inputVariants } from "./style";

export type AllowedInputTypes = "password" | "text" | "number" | "tel";

export type OTPRootProps = PropsWithChildren<
  {
    className?: string;
    /** Value of the OTP input */
    value?: string;
    /** Callback to be called when the OTP value changes */
    onChange: (otp: string) => void;
    /** Callback to be called when pasting content into the component */
    onPaste?: (event: React.ClipboardEvent<HTMLDivElement>) => void;
    /** Whether the first input should be auto focused */
    shouldAutoFocus?: boolean;
    /** The type that will be passed to the input being rendered */
    type?: AllowedInputTypes;
    isInvalid?: boolean;
    disabled?: boolean;
  } & VariantProps<typeof inputVariants>
>;

const Root = ({
  children,
  className,
  onPaste,
  type = "number",
  onChange,
  shouldAutoFocus = false,
  value = "",
  size = "sm",
  isInvalid = false,
  disabled = false,
}: OTPRootProps) => {
  const [activeInput, setActiveInput] = useState(0);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (shouldAutoFocus) {
      inputRefs.current[0]?.focus();
    }
  }, [shouldAutoFocus]);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const contextValue = useMemo<OTPRootContext>(() => {
    return {
      inputRefs,
      value,
      size,
      isInvalid,
      onChange,
      activeInput,
      setActiveInput,
      type,
      disabled,
    };
  }, [activeInput, type, onChange, value, size, isInvalid, disabled]);

  return (
    <OTPRootContextProvider value={contextValue}>
      <div className={cn("flex items-center gap-2", className)} onPaste={onPaste}>
        {children}
      </div>
    </OTPRootContextProvider>
  );
};

export default Root;
